import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import RecipeEditor from './RecipeEditor';
import ChatBox from './ChatBox';
import LiveOutput from './LiveOutput';
import RecipeActivity from './RecipeActivity';
import { RecipeSidebarRef } from './RecipeSidebar';
import { getApiUrl } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import { sanitizeRecipeName } from '../../utils/validation';
import { Recipe } from '../../types/recipe';
import { useAppShell } from '../../contexts/AppShellContext';
import {
  createBlankRecipe,
  getLatestRecipeDraft,
  observeRecipeDraft,
  RecipeDraftJob,
  retryRecipeDraft,
} from '../../services/recipeDraftService';
import { addMessage, getConversations } from '../../services/conversationService';
import { trackEvent } from '../../services/analyticsService';
import DeletedRecipeNotFound from './DeletedRecipeNotFound';
import KitchenLayout from '../Navigation/KitchenLayout';
import AlertDialog from '../Common/AlertDialog';

type WorkspacePanel = 'chat' | 'recipe' | 'output' | 'activity';
type LowerPanel = 'chat' | 'output' | 'activity';

const unsavedRecipe = (): Recipe => ({
  id: 0,
  name: 'New recipe',
  content: '',
  draft_content: null,
  is_active: false,
  planSuspendedAt: null,
  planSuspensionReason: null,
  trigger_type: 'manual',
  draft_trigger_type: null,
  updatedAt: new Date().toISOString(),
  last_save_time: null,
  last_activation_time: null,
  last_execution_at: null,
});

type RecipeEditProps = { newRecipe?: boolean };

const RecipeEdit: React.FC<RecipeEditProps> = ({ newRecipe = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { skin, chefPanelVisible, setChefPanelVisible } = useAppShell();
  const isYolo = skin === 'yolo';
  const recipeIdMatch = location.pathname.match(/^\/recipes\/(\d+)\/edit$/);
  const recipeId = recipeIdMatch ? Number(recipeIdMatch[1]) : null;
  const focusChat = Boolean((location.state as { focusChatInput?: boolean } | null)?.focusChatInput);
  const sidebarRef = useRef<RecipeSidebarRef | null>(null);

  const [recipe, setRecipe] = useState<Recipe | null>(() => newRecipe ? unsavedRecipe() : null);
  const [recipeNotFound, setRecipeNotFound] = useState(false);
  const [newName, setNewName] = useState(newRecipe ? 'New recipe' : '');
  const newDraftContentRef = useRef('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [unsaved, setUnsaved] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
  const [mobilePanel, setMobilePanel] = useState<WorkspacePanel>(focusChat ? 'chat' : 'recipe');
  const [lowerPanel, setLowerPanel] = useState<LowerPanel>('chat');
  const [chefFocusRequest, setChefFocusRequest] = useState(0);
  const [outputFocusRequest, setOutputFocusRequest] = useState(0);
  const [diagnosticsMinimized, setDiagnosticsMinimized] = useState(
    () => localStorage.getItem('recipeDiagnosticsMinimized') === 'true'
  );
  const [draftJob, setDraftJob] = useState<RecipeDraftJob | null>(null);
  const [draftLookupPending, setDraftLookupPending] = useState(Boolean(recipeId) && !newRecipe);
  const [draftStreamError, setDraftStreamError] = useState<string | null>(null);
  const [initialChefActive, setInitialChefActive] = useState(false);
  const [alertDialog, setAlertDialog] = useState<{ title: string; message: string } | null>(null);
  const draftCleanupRef = useRef<(() => void) | null>(null);
  const recipeAbortRef = useRef<AbortController | null>(null);
  const materializeRef = useRef<Promise<Recipe> | null>(null);
  const displayedRecipeIsCurrent = Boolean(recipeId && recipe?.id === recipeId);
  const isRecipeReady = newRecipe || Boolean(recipeId && recipe?.id === recipeId && !draftLookupPending);
  const isSwitchingRecipe = !newRecipe && Boolean(recipeId && !isRecipeReady);
  const draftActive = displayedRecipeIsCurrent
    && Boolean(draftJob && ['queued', 'preparing', 'generating'].includes(draftJob.status));
  const initialAuthoringActive = draftActive || initialChefActive;
  const handleChefJobState = useCallback((jobId: string | null, active: boolean) => {
    if (jobId && jobId === draftJob?.chefJobId) setInitialChefActive(active);
  }, [draftJob?.chefJobId]);

  useEffect(() => {
    if (focusChat) {
      setLowerPanel('chat');
      setMobilePanel('chat');
      setDiagnosticsMinimized(false);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [focusChat, location.pathname, navigate]);

  const refreshRecipe = useCallback(() => {
    recipeAbortRef.current?.abort();
    if (!recipeId || !token) {
      recipeAbortRef.current = null;
      if (newRecipe) {
        setRecipe(current => current || unsavedRecipe());
        setNewName(current => current || 'New recipe');
      } else {
        setRecipe(null);
        setNewName('');
      }
      setRecipeNotFound(false);
      setLastSaveTime(null);
      return;
    }
    const controller = new AbortController();
    recipeAbortRef.current = controller;
    setRecipeNotFound(false);
    void fetch(getApiUrl(`recipes/${recipeId}`), {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then(async response => {
        const data = await response.json();
        if (response.status === 404) {
          setRecipe(null);
          setRecipeNotFound(true);
          return null;
        }
        if (!response.ok) throw new Error(data.message || 'Could not load recipe');
        return data;
      })
      .then(data => {
        if (!data) return;
        setRecipe(data);
        setNewName(data.name);
        setLastSaveTime(data.last_save_time ? new Date(data.last_save_time) : null);
      })
      .catch(error => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        console.error('Error loading recipe:', error);
        setAlertDialog({
          title: 'Could not load recipe',
          message: error instanceof Error ? error.message : 'Please try again.',
        });
      });
  }, [newRecipe, recipeId, token]);

  useEffect(() => {
    refreshRecipe();
    return () => recipeAbortRef.current?.abort();
  }, [refreshRecipe]);

  useEffect(() => {
    draftCleanupRef.current?.();
    draftCleanupRef.current = null;
    setDraftJob(null);
    setDraftLookupPending(Boolean(recipeId) && !newRecipe);
    setDraftStreamError(null);
    setInitialChefActive(false);
    if (!recipeId || newRecipe) return;
    let cancelled = false;
    void getLatestRecipeDraft(recipeId)
      .then(job => { if (!cancelled) setDraftJob(job); })
      .catch(error => { if (!cancelled) console.error('Could not load recipe draft status:', error); })
      .finally(() => { if (!cancelled) setDraftLookupPending(false); });
    return () => {
      cancelled = true;
      draftCleanupRef.current?.();
      draftCleanupRef.current = null;
    };
  }, [newRecipe, recipeId]);

  useEffect(() => {
    if (!draftJob || !draftActive) return;
    draftCleanupRef.current?.();
    draftCleanupRef.current = observeRecipeDraft(draftJob.id, {
      onSnapshot: next => {
        setDraftJob(current => current?.id === next.id && current.partialContent.length > next.partialContent.length
          ? { ...next, partialContent: current.partialContent }
          : next);
        setDraftStreamError(null);
        if (next.chefJobId) document.dispatchEvent(new CustomEvent('refreshConversations'));
      },
      onDelta: partialContent => {
        setDraftJob(current => current ? { ...current, partialContent } : current);
      },
      onComplete: next => {
        draftCleanupRef.current = null;
        setDraftJob(next);
        if (next.chefJobId) setInitialChefActive(true);
        setDraftStreamError(null);
        refreshRecipe();
        sidebarRef.current?.refreshRecipes(false);
        document.dispatchEvent(new CustomEvent('refreshRecipeContent', { detail: { recipeId } }));
        document.dispatchEvent(new CustomEvent('refreshConversations'));
      },
      onError: (error, next) => {
        draftCleanupRef.current = null;
        if (next) setDraftJob(next);
        setDraftStreamError(error.message);
        refreshRecipe();
        if (next?.status === 'failed') {
          document.dispatchEvent(new CustomEvent('refreshRecipeContent', { detail: { recipeId } }));
        }
      },
    });
    return () => {
      draftCleanupRef.current?.();
      draftCleanupRef.current = null;
    };
  // Observe once while the job is active; snapshots update the same job.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftJob?.id, draftActive]);

  useEffect(() => {
    if (!initialAuthoringActive) return;
    setLowerPanel('chat');
    setMobilePanel('chat');
    setDiagnosticsMinimized(false);
  }, [initialAuthoringActive]);

  useEffect(() => {
    if (draftJob && !initialAuthoringActive) setMobilePanel('recipe');
  }, [draftJob, initialAuthoringActive]);

  useEffect(() => {
    const refresh = (event: Event) => {
      const targetRecipeId = (event as CustomEvent<{ recipeId?: number }>).detail?.recipeId;
      if (!targetRecipeId || targetRecipeId === recipeId) {
        refreshRecipe();
        sidebarRef.current?.refreshRecipes(false);
      }
    };
    document.addEventListener('refreshChefMutation', refresh);
    return () => document.removeEventListener('refreshChefMutation', refresh);
  }, [recipeId, refreshRecipe]);

  useEffect(() => localStorage.setItem('recipeDiagnosticsMinimized', String(diagnosticsMinimized)), [diagnosticsMinimized]);

  const materializeRecipe = useCallback((content: string): Promise<Recipe> => {
    if (materializeRef.current) return materializeRef.current;
    const name = sanitizeRecipeName(newName || recipe?.name || 'New recipe') || 'New recipe';
    const request = createBlankRecipe({
      name: name === 'New recipe' ? undefined : name,
      content,
    })
      .then(created => {
        trackEvent('recipe_created', {
          recipe_id: created.id,
          content_length: content.trim().length,
          creation_flow: 'blank_editor',
        });
        sidebarRef.current?.refreshRecipes(false);
        return created;
      })
      .catch(error => {
        materializeRef.current = null;
        throw error;
      });
    materializeRef.current = request;
    return request;
  }, [newName, recipe]);

  const persistLatestNewContent = useCallback(async (created: Recipe): Promise<void> => {
    const latestContent = newDraftContentRef.current.trim();
    if (!latestContent || latestContent === created.content) return;
    const response = await fetch(getApiUrl(`recipes/${created.id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ content: latestContent }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to save recipe');
  }, [token]);

  const save = async (content: string) => {
    if (!recipe || !token) return;
    if (newRecipe) {
      const created = await materializeRecipe(content);
      await persistLatestNewContent(created);
      navigate(`/recipes/${created.id}/edit`, { replace: true });
      return;
    }
    const response = await fetch(getApiUrl(`recipes/${recipe.id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ content }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to save recipe');
  };

  const sendFirstChefMessage = useCallback(async (message: string) => {
    const created = await materializeRecipe(newDraftContentRef.current);
    await persistLatestNewContent(created);
    const conversations = await getConversations(String(created.id));
    const conversation = conversations[0];
    if (!conversation) throw new Error('Chef is unavailable for this recipe.');
    await addMessage(conversation.id, 'user', message);
    navigate(`/recipes/${created.id}/edit`, { replace: true, state: { focusChatInput: true } });
  }, [materializeRecipe, navigate, persistLatestNewContent]);

  const handleNewContentChange = useCallback((content: string) => {
    newDraftContentRef.current = content;
  }, []);

  const retryDraft = async () => {
    if (!draftJob) return;
    setDraftStreamError(null);
    try {
      const next = await retryRecipeDraft(draftJob.id);
      setDraftJob(next);
    } catch (error: any) {
      setDraftStreamError(error?.response?.data?.message || error?.message || 'Could not retry the recipe draft');
    }
  };

  const rename = async () => {
    if (!recipe || !token) return;
    const name = sanitizeRecipeName(newName);
    if (!name || name === recipe.name) {
      setEditingTitle(false);
      setNewName(recipe.name);
      return;
    }
    if (newRecipe) {
      setRecipe({ ...recipe, name });
      setNewName(name);
      setEditingTitle(false);
      return;
    }
    const response = await fetch(getApiUrl(`recipes/${recipe.id}/rename`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ newName: name }),
    });
    const data = await response.json();
    if (!response.ok) {
      setAlertDialog({
        title: 'Could not rename recipe',
        message: data.message || 'Please try again.',
      });
      setNewName(recipe.name);
    } else {
      setRecipe({ ...recipe, name });
      sidebarRef.current?.refreshRecipes(false);
    }
    setEditingTitle(false);
  };

  const editorHeader = (
    <div className="flex min-h-[58px] shrink-0 items-center justify-between gap-3 border-b border-gray-200 px-5 py-2.5">
      {editingTitle ? (
        <div className="flex min-w-0 flex-1 gap-2">
          <input
            value={newName}
            onChange={event => setNewName(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') void rename();
              if (event.key === 'Escape') setEditingTitle(false);
            }}
            autoFocus
            className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-lg font-semibold"
          />
          <button onClick={() => void rename()} className="rounded-lg bg-blue-600 px-3 text-sm font-medium text-white">Save</button>
        </div>
      ) : (
        <button disabled={draftActive} onClick={() => setEditingTitle(true)} className="min-w-0 truncate text-left text-xl font-semibold text-gray-950 disabled:cursor-default">
          {draftActive ? (draftJob?.proposedName || 'Creating recipe…') : (recipe?.name || 'Loading…')} {!draftActive && <span className="text-sm font-normal text-gray-400">Edit</span>}
        </button>
      )}
      <div className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${unsaved ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
        {unsaved ? 'Unsaved changes' : lastSaveTime ? `Saved ${lastSaveTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : 'Not saved yet'}
      </div>
    </div>
  );

  const editor = (
    <div className={`flex h-full min-h-0 flex-col ${isYolo ? 'bg-[#f7f3e8]' : 'bg-white'}`}>
      {!isYolo && editorHeader}
      <div className={`min-h-0 flex-1 ${isYolo ? 'overflow-visible bg-[#f7f3e8]' : 'overflow-y-auto'}`}>
        <RecipeEditor
          key={`editor-${recipe?.id}`}
          initialRecipe={recipe!}
          recipeName={draftActive ? (draftJob?.proposedName || 'Creating recipe…') : recipe?.name}
          triggerType={(draftActive && draftJob?.proposedTriggerType) || recipe?.draft_trigger_type || recipe?.trigger_type || 'manual'}
          onSave={save}
          onUnsavedChangesChange={setUnsaved}
          onLastSaveTimeChange={setLastSaveTime}
          sidebarRef={sidebarRef}
          onStatusChange={updatedRecipe => sidebarRef.current?.updateRecipe(updatedRecipe)}
          onChefAttention={(focusInput = false) => {
            setChefPanelVisible(true);
            setLowerPanel('chat');
            setMobilePanel('chat');
            setDiagnosticsMinimized(false);
            if (focusInput) setChefFocusRequest(current => current + 1);
          }}
          onOpenLiveOutput={(focusOutput = false) => {
            setChefPanelVisible(true);
            setLowerPanel('output');
            setMobilePanel('output');
            setDiagnosticsMinimized(false);
            if (focusOutput) setOutputFocusRequest(current => current + 1);
          }}
          chefPanelVisible={chefPanelVisible}
          onToggleChefPanel={() => setChefPanelVisible(current => !current)}
          generatedContent={draftActive ? draftJob?.partialContent : undefined}
          generationActive={draftActive}
          generationStatus={draftJob?.statusText}
          generationError={draftJob?.status === 'failed' ? (draftStreamError || draftJob.errorMessage) : null}
          onRetryGeneration={retryDraft}
          onContentChange={newRecipe ? handleNewContentChange : undefined}
        />
      </div>
    </div>
  );

  const lowerWorkspace = (
    <div id="recipe-chef-panel" className={`flex h-full min-h-0 flex-col bg-white ${isYolo ? 'overflow-hidden rounded-2xl border border-[#eadfca] shadow-[0_12px_32px_rgba(83,63,32,0.08)]' : 'border-t border-gray-200'}`}>
      <div className={`flex shrink-0 items-start ${isYolo ? 'gap-1 border-b border-[#eee4d2] bg-[#fffaf0] p-2' : 'border-b border-gray-200 bg-gray-50 px-2 pt-1'}`}>
        {(['chat', 'output', 'activity'] as const).map(panel => (
          <button
            key={panel}
            onClick={() => {
              setLowerPanel(panel);
              setDiagnosticsMinimized(false);
            }}
            className={`${isYolo ? 'rounded-full px-4 py-1.5' : 'rounded-t-lg px-4 py-2'} text-sm font-semibold transition ${lowerPanel === panel
              ? isYolo ? 'bg-amber-100 text-amber-900 shadow-sm' : 'border border-b-white bg-white text-blue-700'
              : isYolo ? 'text-stone-500 hover:bg-white hover:text-stone-800' : 'text-gray-500'}`}
          >
            {panel === 'chat' ? 'Chef' : panel === 'output' ? 'Live output' : 'Activity'}
          </button>
        ))}
        {!isYolo && <button
          type="button"
          onClick={() => setDiagnosticsMinimized(current => !current)}
          className="ml-auto mt-0.5 rounded-md p-2 text-gray-500 hover:bg-gray-200 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label={diagnosticsMinimized ? 'Expand lower workspace panel' : 'Minimize lower workspace panel'}
          aria-expanded={!diagnosticsMinimized}
          title={diagnosticsMinimized ? 'Expand panel' : 'Minimize panel'}
        >
          {diagnosticsMinimized ? (
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
              <path d="m5.5 12.5 4.5-4.5 4.5 4.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
              <path d="M5 12.5h10" strokeLinecap="round" />
            </svg>
          )}
        </button>}
      </div>
      <div className={`min-h-0 flex-1 overflow-hidden ${diagnosticsMinimized && !isYolo ? 'hidden' : ''}`}>
        {lowerPanel === 'chat' && <ChatBox key={`chat-${newRecipe ? 'new' : recipe?.id}`} recipeId={newRecipe ? undefined : recipe?.id} recipeName={recipe?.name} recipeGenerationActive={draftActive} onJobStateChange={handleChefJobState} onFirstSend={newRecipe ? sendFirstChefMessage : undefined} focusRequest={chefFocusRequest} />}
        {lowerPanel === 'output' && <LiveOutput key={`output-${recipe?.id}`} recipeId={recipe?.id} focusRequest={outputFocusRequest} />}
        {lowerPanel === 'activity' && <RecipeActivity key={`activity-${recipe?.id}`} recipeId={recipe?.id} />}
      </div>
    </div>
  );

  return (
    <KitchenLayout
      title={newRecipe ? (recipe?.name || 'New recipe') : recipeId ? (draftActive ? (draftJob?.proposedName || 'Creating recipe…') : (recipe?.name || 'Recipe')) : 'Recipes'}
      selectedRecipe={recipe?.name}
      sidebarRef={sidebarRef}
      className={isYolo ? 'bg-[#f7f3e8]' : 'bg-gray-100'}
    >

      {alertDialog && (
        <AlertDialog
          title={alertDialog.title}
          message={alertDialog.message}
          onClose={() => setAlertDialog(null)}
        />
      )}

      {recipeId && recipeNotFound && <DeletedRecipeNotFound recipeId={recipeId} />}

      {(newRecipe || recipeId) && !recipeNotFound && <main className={`hidden min-w-0 flex-1 lg:flex ${isYolo ? 'justify-center bg-[#f7f3e8] px-3 pb-3 pt-12' : ''}`}>
        <section className={`relative flex min-w-0 flex-col ${isYolo ? 'w-3/5' : 'flex-1'}`} aria-busy={isSwitchingRecipe}>
          {isSwitchingRecipe && (
            <div className="pointer-events-none absolute left-1/2 top-3 z-20 -translate-x-1/2 rounded-full border border-gray-200 bg-white/95 px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm" role="status">
              Loading recipe…
            </div>
          )}
          <div className={`flex min-h-0 flex-1 flex-col transition-opacity duration-150 ${isYolo ? 'gap-3' : ''} ${isSwitchingRecipe ? 'pointer-events-none opacity-60' : 'opacity-100'}`}>
            <div className={`min-h-0 ${isYolo
              ? chefPanelVisible ? 'w-full flex-1' : 'w-full basis-[70%] shrink-0'
              : chefPanelVisible
                ? diagnosticsMinimized ? 'flex-1 overflow-hidden' : 'basis-2/3 overflow-hidden'
                : 'basis-[70%] shrink-0 overflow-hidden'}`}>
              {recipe ? editor : <div className={`h-full animate-pulse ${isYolo ? 'rounded-2xl bg-[#fffaf0]' : 'bg-white'}`} />}
            </div>
            {chefPanelVisible && (isYolo
              ? <div className="min-h-0 flex-1">{recipe ? lowerWorkspace : <div className="h-full animate-pulse rounded-2xl bg-[#fffaf0]" />}</div>
              : <div className={`min-h-0 overflow-hidden ${diagnosticsMinimized ? 'h-11 shrink-0' : 'basis-1/3'}`}>
                  {recipe ? lowerWorkspace : <div className="h-full animate-pulse border-t border-gray-200 bg-gray-50" />}
                </div>)}
          </div>
        </section>
      </main>}

      {(newRecipe || recipeId) && !recipeNotFound && <main className="relative flex min-w-0 flex-1 flex-col lg:hidden" aria-busy={isSwitchingRecipe}>
        {isSwitchingRecipe && (
          <div className="pointer-events-none absolute left-1/2 top-3 z-20 -translate-x-1/2 rounded-full border border-gray-200 bg-white/95 px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm" role="status">
            Loading recipe…
          </div>
        )}
        <div className={`flex min-h-0 flex-1 flex-col transition-opacity duration-150 ${isSwitchingRecipe ? 'pointer-events-none opacity-60' : 'opacity-100'}`}>
          <nav className={`grid shrink-0 grid-cols-4 border-b ${isYolo ? 'border-[#eadfca] bg-[#fffaf0]' : 'border-gray-200 bg-white'}`} aria-label="Recipe workspace">
            {(['recipe', 'chat', 'output', 'activity'] as WorkspacePanel[]).map(panel => (
              <button
                key={panel}
                onClick={() => setMobilePanel(panel)}
                className={`min-h-11 px-1 py-2.5 text-xs font-semibold capitalize ${mobilePanel === panel
                  ? isYolo ? 'border-b-2 border-amber-600 text-amber-800' : 'border-b-2 border-blue-600 text-blue-700'
                  : isYolo ? 'text-stone-500' : 'text-gray-500'}`}
              >
                {panel === 'chat' ? 'Chef' : panel === 'output' ? 'Output' : panel}
              </button>
            ))}
          </nav>
          <div className={`min-h-0 flex-1 overflow-hidden ${isYolo ? 'bg-[#f7f3e8]' : ''}`}>
            {!recipe ? (
              <div className={`h-full animate-pulse ${isYolo ? 'bg-[#fffaf0]' : 'bg-white'}`} />
            ) : <>
              {mobilePanel === 'chat' && <ChatBox key={`mobile-chat-${newRecipe ? 'new' : recipe?.id}`} recipeId={newRecipe ? undefined : recipe?.id} recipeName={recipe?.name} recipeGenerationActive={draftActive} onJobStateChange={handleChefJobState} onFirstSend={newRecipe ? sendFirstChefMessage : undefined} focusRequest={chefFocusRequest} />}
              {mobilePanel === 'recipe' && editor}
              {mobilePanel === 'output' && <LiveOutput key={`mobile-output-${recipe?.id}`} recipeId={recipe?.id} focusRequest={outputFocusRequest} />}
              {mobilePanel === 'activity' && <RecipeActivity key={`mobile-activity-${recipe?.id}`} recipeId={recipe?.id} />}
            </>}
          </div>
        </div>
      </main>}
    </KitchenLayout>
  );
};

export default RecipeEdit;
